// MCHD Room Scheduler - Building Form Component
// Form for creating and editing buildings

import React, { useEffect } from 'react';
import {
  Modal,
  Paper,
  Title,
  TextInput,
  Textarea,
  Switch,
  Button,
  Group,
  Stack,
  Alert,
  LoadingOverlay,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useBuildingById } from '../../hooks/admin/useBuildings';
import { validateBuildingData } from '../../api/endpoints/buildings';

// =============================================================================
// TYPES
// =============================================================================

interface BuildingFormData {
  name: string;
  description?: string | null;
  isActive?: boolean;
}

interface BuildingFormProps {
  mode: 'create' | 'edit';
  buildingId?: string;
  onSubmit: (data: BuildingFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

// =============================================================================
// FORM VALIDATION SCHEMA
// =============================================================================

const formValidation = {
  name: (value: string) => {
    if (!value || value.trim().length === 0) {
      return 'Building name is required';
    }
    if (value.trim().length > 100) {
      return 'Building name cannot exceed 100 characters';
    }
    return null;
  },
  description: (value: string | null | undefined) => {
    if (value && value.length > 500) {
      return 'Building description cannot exceed 500 characters';
    }
    return null;
  },
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Building Form Component
 * Provides form interface for creating and editing buildings
 * 
 * @param props - Component props
 * @returns Building form interface
 */
export function BuildingForm({
  mode,
  buildingId,
  onSubmit,
  onCancel,
  isLoading = false,
}: BuildingFormProps): React.ReactElement {
  // =============================================================================
  // FORM SETUP
  // =============================================================================
  
  const form = useForm<BuildingFormData>({
    initialValues: {
      name: '',
      description: '',
      isActive: true,
    },
    validate: {
      name: formValidation.name,
      description: formValidation.description,
    },
    transformValues: (values) => ({
      ...values,
      name: values.name.trim(),
      description: values.description?.trim() || null,
    }),
  });

  // =============================================================================
  // DATA FETCHING (for edit mode)
  // =============================================================================
  
  const {
    data: buildingData,
    isLoading: isLoadingBuilding,
    error: buildingError,
  } = useBuildingById(buildingId || '');

  // =============================================================================
  // EFFECTS
  // =============================================================================
  
  // Load building data for edit mode
  useEffect(() => {
    if (mode === 'edit' && buildingData?.data?.building) {
      const building = buildingData.data.building;
      form.setValues({
        name: building.name,
        description: building.description || '',
        isActive: building.isActive,
      });
    }
  }, [mode, buildingData, form]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle form submission
   * @param values - Form values
   */
  const handleSubmit = async (values: BuildingFormData) => {
    try {
      // Validate data before submission
      const validation = validateBuildingData(values);
      if (!validation.isValid) {
        form.setErrors({
          name: validation.errors.find(e => e.includes('name')) || undefined,
          description: validation.errors.find(e => e.includes('description')) || undefined,
        });
        return;
      }

      await onSubmit(values);
    } catch (error) {
      console.error('Error submitting building form:', error);
    }
  };

  /**
   * Handle form cancellation
   */
  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  // =============================================================================
  // LOADING STATES
  // =============================================================================
  
  const isFormLoading = isLoading || (mode === 'edit' && isLoadingBuilding);

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Modal
      opened
      onClose={handleCancel}
      title={
        <Title order={3}>
          {mode === 'create' ? 'Create New Building' : 'Edit Building'}
        </Title>
      }
      size="md"
      centered
    >
      <Paper>
        <LoadingOverlay visible={isFormLoading} />
        
        {buildingError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error Loading Building"
            color="red"
            mb="md"
          >
            {buildingError.message}
          </Alert>
        )}

        <form onSubmit={form.onSubmit((values) => handleSubmit(values as BuildingFormData))}>
          <Stack gap="md">
            {/* Building Name */}
            <TextInput
              label="Building Name"
              placeholder="Enter building name"
              required
              {...form.getInputProps('name')}
              disabled={isFormLoading}
            />

            {/* Building Description */}
            <Textarea
              label="Description"
              placeholder="Enter building description (optional)"
              minRows={3}
              maxRows={6}
              {...form.getInputProps('description')}
              disabled={isFormLoading}
            />

            {/* Active Status */}
            <Switch
              label="Active"
              description="Building is active and available for use"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
              disabled={isFormLoading}
            />

            {/* Action Buttons */}
            <Group justify="flex-end" mt="lg">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isFormLoading}
              >
                Cancel
              </Button>
              
              <Button
                type="submit"
                loading={isFormLoading}
                disabled={!form.isValid()}
              >
                {mode === 'create' ? 'Create Building' : 'Update Building'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Modal>
  );
}
